import React, { useMemo } from 'react';
import ReactDOM from 'react-dom/client';
import { Card, Button, Descriptions, Table, Row, Col, Typography, Tag, App, Spin, Space } from 'antd';
import { ArrowLeftOutlined, EditOutlined, CheckCircleOutlined, StopOutlined, PrinterOutlined } from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { usePurchaseRequisition, useSubmitPurchaseRequisition, useApprovePurchaseRequisition, useCancelPurchaseRequisition } from '@/hooks/usePurchaseRequisition';
import { ApprovalPanel } from '@/components/approval/ApprovalPanel';
import { LineSpecificationsText } from '@/components/procurement/LineSpecifications';
import { ENTITY_TYPE_PURCHASE_REQUISITION } from '@/constants/approval-entity-types';
import purchaseRequisitionService from '@/services/purchaseRequisition.service';
import { supplierInquiryService } from '@/services/supplierInquiry.service';
import { useCurrency } from '@/contexts/CurrencyContext';
import { useDepartmentsForSelection } from '@/hooks/useDepartments';
import { useUserDisplayResolver } from '@/hooks/useUserDisplayResolver';
import { TaskRoutingCard } from '@/components/unified/TaskRoutingCard';
import ProcurementAttachmentsCard from '@/components/procurement/ProcurementAttachmentsCard';
import { PrintablePurchaseRequisition } from '@/components/procurement/PrintablePurchaseRequisition';
import ReportDocumentActions from '@/components/finance/report-document/ReportDocumentActions';
import { DOCUMENT_ENTITY_TYPE_PURCHASE_REQUISITION } from '@/constants/document-entity-types';
import { BudgetCodeLabel } from '@/components/project/BudgetCodeLabel';
import { DocumentRefLink } from '@/components/DocumentRefLink';
import {
  getCriticalItemRowClassName,
  ItemCodeWithCritical,
} from '@/components/inventory/criticalItemUi';
import { useCriticalItemLookup } from '@/hooks/useCriticalItemLookup';
import SpotPurchasePanel from '@/components/procurement/SpotPurchasePanel';
import { formatItemDescriptionWithProfile } from '@/components/inventory/ItemSelect';
import { documentLineDimensions } from '@/utils/inventory/measurementQuantity';
import { EngineeringWeightCell } from '@/components/procurement/EngineeringWeightCell';

const { Title, Text } = Typography;

const PurchaseRequestDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { message } = App.useApp();
  const { formatCurrency } = useCurrency();
  const { resolve } = useUserDisplayResolver();
  const { isCriticalCode } = useCriticalItemLookup();
  const { data: pr, isLoading } = usePurchaseRequisition(id);
  const { data: departments = [] } = useDepartmentsForSelection();
  const submitMutation = useSubmitPurchaseRequisition();
  const approveMutation = useApprovePurchaseRequisition();
  const cancelMutation = useCancelPurchaseRequisition();

  const { data: linkedInquiries = [] } = useQuery({
    queryKey: ['supplier-inquiries', 'by-pr', pr?.pr_number],
    queryFn: () => supplierInquiryService.list({ pr_number: pr!.pr_number }),
    enabled: !!pr?.pr_number,
    staleTime: 30000,
  });

  const sourceSalesOrders = useMemo(() => {
    const map = new Map<string, string>();
    for (const item of pr?.items ?? []) {
      const soId = (item as any).sales_order_id as string | undefined;
      const soNumber = (item as any).salesOrder?.sales_order_number as string | undefined;
      if (soId) map.set(soId, soNumber || '');
    }
    return [...map.entries()].map(([soId, soNumber]) => ({ soId, soNumber }));
  }, [pr?.items]);

  if (isLoading || !id) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
        <Spin size="large" />
      </div>
    );
  }
  if (!pr) {
    return (
      <Card>
        <Text type="secondary">Purchase Request not found</Text>
      </Card>
    );
  }

  const prCurrency = pr.currency ?? undefined;
  const fmt = (amount: number) => formatCurrency(amount, prCurrency);

  const isDraft = pr.pr_status === 'Draft';
  const isSubmitted = pr.pr_status === 'Submitted';
  const approvalPending = (pr.approval_status ?? 'Pending') === 'Pending';
  const showUniversalApproval = !!pr.approval_instance_id;

  const handlePrint = () => {
    const mainContent = document.getElementById('root');
    if (mainContent) mainContent.style.display = 'none';
    const printContainer = document.createElement('div');
    printContainer.id = 'print-container';
    printContainer.style.display = 'block';
    document.body.appendChild(printContainer);
    const root = ReactDOM.createRoot(printContainer);
    const cleanup = () => {
      root.unmount();
      if (document.body.contains(printContainer)) document.body.removeChild(printContainer);
      if (mainContent) mainContent.style.display = 'block';
      window.removeEventListener('afterprint', cleanup);
    };
    root.render(<PrintablePurchaseRequisition pr={pr} onClose={cleanup} />);
    window.addEventListener('afterprint', cleanup);
  };

  return (
    <div style={{ padding: 24 }}>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Button type="text" icon={<ArrowLeftOutlined />} onClick={() => navigate('/scm/procurement/purchase-requests')}>
            Back
          </Button>
          <Title level={4} style={{ margin: '8px 0 0' }}>
            Purchase Request {pr.pr_number}
          </Title>
          <Tag
            color={
              pr.pr_status === 'Approved'
                ? 'green'
                : pr.pr_status === 'Rejected'
                  ? 'orange'
                  : pr.pr_status === 'Submitted'
                    ? 'blue'
                    : pr.pr_status === 'Cancelled'
                      ? 'red'
                      : 'default'
            }
          >
            {pr.pr_status}
          </Tag>
        </Col>
        <Col>
          <Button icon={<PrinterOutlined />} onClick={handlePrint} style={{ marginRight: 8 }}>
            Print
          </Button>
          <span style={{ display: 'inline-block', marginRight: 8 }}>
            <ReportDocumentActions
              reportKey="purchase-requisition"
              params={{ id: pr.id }}
              documentId={pr.id}
              sendPermissions={['procurement.pr.edit', 'procurement.po.edit']}
            />
          </span>
          {isDraft && (
            <>
              <Button icon={<EditOutlined />} onClick={() => navigate(`/scm/procurement/purchase-requests/${id}/edit`)}>
                Edit
              </Button>
              <Button type="primary" icon={<CheckCircleOutlined />} onClick={() => submitMutation.mutate(id!)} loading={submitMutation.isPending}>
                Submit
              </Button>
            </>
          )}
          {isSubmitted && approvalPending && !showUniversalApproval && (
            <Button type="primary" icon={<CheckCircleOutlined />} onClick={() => approveMutation.mutate(id!)} loading={approveMutation.isPending}>
              Approve
            </Button>
          )}
          {(isDraft || (isSubmitted && approvalPending)) && (
            <Button danger icon={<StopOutlined />} onClick={() => cancelMutation.mutate(id!)} loading={cancelMutation.isPending}>
              Cancel
            </Button>
          )}
        </Col>
      </Row>

      <TaskRoutingCard
        sourceDocumentType="PurchaseRequisition"
        sourceDocumentId={id!}
        nextActionUserId={(pr as { next_action_user_id?: string | null }).next_action_user_id}
        documentOwnerLabel="Requested by"
        documentOwnerUserId={pr.requested_by}
        disabled={pr.pr_status === 'Cancelled' || pr.pr_status === 'Closed'}
        invalidateQueryKeys={[['purchase-requisitions', id]]}
      />

      {showUniversalApproval && (
        <div style={{ marginBottom: 16 }}>
          <ApprovalPanel
            entityType={ENTITY_TYPE_PURCHASE_REQUISITION}
            entityId={id!}
            onApproveViaDocument={async (remarks) => {
              await purchaseRequisitionService.approve(id!, remarks);
            }}
            onRejectViaDocument={async (payload) => {
              await purchaseRequisitionService.reject(id!, payload);
            }}
            extraInvalidateQueryKeys={[['purchase-requisitions'], ['purchase-requisitions', id]]}
          />
        </div>
      )}

      <SpotPurchasePanel prId={id!} prStatus={pr.pr_status} />

      <Card title="Header" style={{ marginBottom: 16 }}>
        {(sourceSalesOrders.length > 0 || linkedInquiries.length > 0) && (
          <div style={{ marginBottom: 12 }}>
            <Text strong>Document links: </Text>
            <Space wrap size="small">
              {sourceSalesOrders.map(({ soId, soNumber }) => (
                <DocumentRefLink
                  key={soId}
                  to={`/growth/orders/${soId}`}
                  number={soNumber || undefined}
                  id={soId}
                />
              ))}
              {linkedInquiries.map((inq) => (
                <DocumentRefLink
                  key={inq.id}
                  to={`/scm/procurement/supplier-inquiries/${inq.id}`}
                  number={inq.inquiry_number}
                  id={inq.id}
                />
              ))}
            </Space>
          </div>
        )}
        <Descriptions column={{ xs: 1, sm: 2, md: 3 }} size="small">
          <Descriptions.Item label="PR Date">{pr.pr_date ? dayjs(pr.pr_date).format('DD MMM YYYY') : '-'}</Descriptions.Item>
          <Descriptions.Item label="Required Date">{pr.required_date ? dayjs(pr.required_date).format('DD MMM YYYY') : '-'}</Descriptions.Item>
          <Descriptions.Item label="Creation Mode">{pr.creation_mode === 'AgainstSalesOrder' ? 'From Customer Order' : 'Direct'}</Descriptions.Item>
          <Descriptions.Item label="Priority">{pr.priority ?? '-'}</Descriptions.Item>
          <Descriptions.Item label="Currency">{pr.currency ?? '-'}</Descriptions.Item>
          <Descriptions.Item label="Department">
            {(pr as any).department_id
              ? (departments.find((d) => d.id === (pr as any).department_id)?.department_name ?? pr.department ?? '-')
              : (pr.department ?? '-')}
          </Descriptions.Item>
          <Descriptions.Item label="Project">{pr.project?.project_name ?? '—'}</Descriptions.Item>
          <Descriptions.Item label="Requested By">
            {(pr as any).requestedByUser?.full_name ??
              (pr as any).requestedByUser?.email ??
              resolve(pr.requested_by, '—')}
          </Descriptions.Item>
          <Descriptions.Item label="Estimated Total">{fmt(Number(pr.estimated_total))}</Descriptions.Item>
          <Descriptions.Item label="Notes" span={2}>{pr.notes ?? '-'}</Descriptions.Item>
        </Descriptions>
      </Card>

      {pr.items?.length ? (
        <Card title="Lines" style={{ marginBottom: 16 }}>
          <Table
            size="small"
            rowKey="id"
            dataSource={pr.items}
            pagination={false}
            rowClassName={(r) =>
              getCriticalItemRowClassName({ is_critical: isCriticalCode(r.item_code) })
            }
            columns={[
              { title: '#', dataIndex: 'line_number', key: 'line_number', width: 50 },
              {
                title: 'Item',
                key: 'code',
                width: 140,
                render: (_, r) =>
                  r.item_code ? (
                    <Space size={6} wrap>
                      <ItemCodeWithCritical code={r.item_code} critical={isCriticalCode(r.item_code)} />
                      {r.line_kind === 'description_only' ? <Text type="secondary">(Non-Stock)</Text> : null}
                    </Space>
                  ) : (
                    '—'
                  ),
              },
              {
                title: 'Item Name',
                key: 'item_name',
                width: 180,
                render: (_, r) => {
                  const name =
                    r.line_kind === 'description_only'
                      ? (r.item_description || '').trim()
                      : (r.item_name || '').trim();
                  return name || '—';
                },
              },
              {
                title: 'Description',
                key: 'desc',
                render: (_, r) => {
                  const desc =
                    formatItemDescriptionWithProfile(r.item_description, {
                      profile_grade: r.profile_grade,
                      profile_heat_no: r.profile_heat_no,
                      profile_dimension: r.profile_dimension,
                      profile_length: r.profile_length,
                    }) || r.item_description;
                  return (
                    <Space direction="vertical" size={2}>
                      <span>{desc || '—'}</span>
                      <LineSpecificationsText value={r.specifications} />
                    </Space>
                  );
                },
              },
              {
                title: 'Dimensions',
                key: 'dimensions',
                width: 130,
                render: (_, r) => documentLineDimensions(r) || '—',
              },
              {
                title: 'Pieces',
                key: 'piece_quantity',
                width: 70,
                align: 'right',
                render: (_, r) => (r.piece_quantity != null ? Number(r.piece_quantity) : '—'),
              },
              { title: 'Quantity', dataIndex: 'requested_quantity', key: 'requested_quantity', width: 90, align: 'right', render: (v: number) => Number(v) },
              { title: 'UOM', dataIndex: 'uom', key: 'uom', width: 60 },
              ...((pr.items ?? []).some((l: any) => l.theoretical_weight != null)
                ? [
                    {
                      title: 'Theo. weight',
                      key: 'theoretical_weight',
                      width: 110,
                      align: 'right' as const,
                      render: (_: unknown, r: any) => (
                        <EngineeringWeightCell weight={r.theoretical_weight} snapshot={r.engineering_snapshot} />
                      ),
                    },
                  ]
                : []),
              { title: 'Project', key: 'project', render: (_, r) => (r as any).project?.project_name ?? r.project_id ?? '-' },
              {
                title: 'WBS',
                key: 'wbs',
                width: 140,
                ellipsis: true,
                render: (_: unknown, r: any) => (
                  <BudgetCodeLabel
                    projectId={r.project_id || (pr as any).project_id}
                    nodeId={r.wbs_node_id}
                    kind="wbs"
                  />
                ),
              },
              {
                title: 'Cat',
                key: 'cat',
                width: 90,
                render: (_: unknown, r: any) => r.cost_category || '—',
              },
              {
                title: 'Source SO',
                key: 'so',
                render: (_, r) => {
                  const soId = (r as any).sales_order_id as string | undefined;
                  const soNumber = (r as any).salesOrder?.sales_order_number as string | undefined;
                  if (!soId) return '-';
                  return (
                    <DocumentRefLink
                      to={`/growth/orders/${soId}`}
                      number={soNumber || undefined}
                      id={soId}
                    />
                  );
                },
              },
              { title: 'Est. Rate', dataIndex: 'estimated_unit_price', key: 'estimated_unit_price', width: 110, align: 'right', render: (v: number) => fmt(Number(v)) },
              { title: 'Est. Total', dataIndex: 'estimated_total', key: 'estimated_total', width: 110, align: 'right', render: (v: number) => fmt(Number(v)) },
            ]}
          />
        </Card>
      ) : null}

      <ProcurementAttachmentsCard
        entityType={DOCUMENT_ENTITY_TYPE_PURCHASE_REQUISITION}
        entityId={id}
        readOnly={pr.pr_status === 'Cancelled' || pr.pr_status === 'Closed'}
        queryKeyPrefix={['documents', 'purchase-requisition']}
      />
    </div>
  );
};

export default PurchaseRequestDetailPage;
